import {
  Component,
  EventEmitter,
  Input,
  Output,
  type OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { HotkeysService, Hotkey } from 'angular2-hotkeys';
import { compact } from 'lodash';
import { type WithRef } from '@principle-theorem/shared';
import { type IChartedItemConfiguration } from '@principle-theorem/principle-core/interfaces';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import { combineLatest, type Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-quick-chart-bar',
  templateUrl: './quick-chart-bar.component.html',
  styleUrls: ['./quick-chart-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuickChartBarComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _registeredHotkeys: Hotkey[] = [];
  items$ = new ReplaySubject<WithRef<IChartedItemConfiguration>[]>(1);
  hotkeyMap$ = new ReplaySubject<string[]>(1);
  hotkeys$: Observable<Hotkey[]>;
  trackByItem = TrackByFunctions.ref<WithRef<IChartedItemConfiguration>>();
  @Output() chartableAdded: EventEmitter<WithRef<IChartedItemConfiguration>> =
    new EventEmitter<WithRef<IChartedItemConfiguration>>();

  @Input()
  set items(items: WithRef<IChartedItemConfiguration>[]) {
    if (items) {
      this.items$.next(items);
    }
  }

  @Input()
  set hotkeyMap(hotkeyMap: string[]) {
    if (hotkeyMap) {
      this.hotkeyMap$.next(hotkeyMap);
    }
  }

  constructor(private _hotkeyService: HotkeysService) {
    this.hotkeys$ = combineLatest([this.items$, this.hotkeyMap$]).pipe(
      map(([items, hotkeyMap]) => {
        const hotkeys: (Hotkey | undefined)[] = items.map(
          (item, index: number) => {
            const hotkey: string | undefined = hotkeyMap[index];
            if (!hotkeyMap[index]) {
              return;
            }
            return this._createHotkey(item, hotkey);
          }
        );
        return compact(hotkeys);
      })
    );

    this.hotkeys$.pipe(takeUntil(this._onDestroy$)).subscribe((hotkeys) => {
      if (this._registeredHotkeys.length) {
        this._hotkeyService.remove(this._registeredHotkeys);
      }

      if (!hotkeys.length) {
        this._registeredHotkeys = [];
        return;
      }

      this._hotkeyService.add(hotkeys);
      this._registeredHotkeys = hotkeys;
    });
  }

  ngOnDestroy(): void {
    if (this._registeredHotkeys.length) {
      this._hotkeyService.remove(this._registeredHotkeys);
    }
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  addChartedItem(item: WithRef<IChartedItemConfiguration>): void {
    this.chartableAdded.emit(item);
  }

  private _createHotkey(
    item: WithRef<IChartedItemConfiguration>,
    hotkey: string
  ): Hotkey {
    return new Hotkey(
      hotkey,
      (): boolean => {
        this.addChartedItem(item);
        return false;
      },
      undefined,
      `Quick chart ${item.name}`
    );
  }
}
